import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';
import { Button, Step, StepByStepContextValue, StepByStepProvider } from 'yoso';
import { QueryErrorBoundary } from '~/components/Error';
import { Close } from '~/components/Icon/Close';
import { LevelUpErrorFallback } from '~/components/LevelUp/Error/LevelUpErrorFallback';
import { LevelUpValues } from '~/components/LevelUp/LevelUp';
import {
  LevelUpStatus,
  LevelUpStep,
  LevelUpToken,
  steps,
} from '~/components/LevelUp/LevelUp.hooks';
import { LevelUpLoading } from '~/components/LevelUp/Popup/Loading/LevelUpLoading';
import { LevelUpResult } from '~/components/LevelUp/Popup/Result';
import { LevelUpTfa } from '~/components/LevelUp/Popup/Tfa';
import {
  LevelUpTokenIssuer,
  LevelUpTokenIssuerProps,
} from '~/components/LevelUp/Popup/Token/LevelUpTokenIssuer';
import {
  BottomSheet,
  BottomSheetProps,
  styles as bottomSheetStyles,
} from '~/components/Modal/BottomSheet';
import modalStyles from '~/components/Modal/Modal.module.scss';
import styles from './LevelUpBottomSheet.module.scss';

export type LevelUpBottomSheetProps = Omit<BottomSheetProps, 'id'> &
  LevelUpValues & {
    onConfirm?: (data: LevelUpStatus) => void;
    onDismiss?: (reason: { step?: LevelUpStep }) => void;
  };

export const LevelUpBottomSheet = ({
  level,
  tokenIdList,
  className,
  onConfirm,
  onDismiss,
  ...props
}: LevelUpBottomSheetProps) => {
  const [token, setToken] = useState<LevelUpToken>({
    authUri: '',
    id: 0,
  });
  const sbsRef = useRef<StepByStepContextValue<LevelUpStep>>(null);
  const onTokenSuccess: LevelUpTokenIssuerProps['onSuccess'] = useCallback((data) => {
    setToken(data);
    sbsRef.current?.forward();
  }, []);

  return (
    <StepByStepProvider steps={steps} ref={sbsRef}>
      {({ step, forward }) => (
        <BottomSheet
          className={classNames(styles.root, styles[step], className)}
          size="full"
          closeButton={({ confirm, dismiss }) =>
            step !== 'LOADING' && (
              <Button
                className={classNames(modalStyles.closeButton, bottomSheetStyles.closeButton)}
                variant="transparent"
                onClick={step === 'COMPLETED' ? confirm : dismiss}>
                <Close />
              </Button>
            )
          }
          onDismiss={onDismiss}
          onConfirm={onConfirm}
          {...props}>
          <QueryErrorBoundary
            fallbackRender={(values) => (
              <LevelUpErrorFallback className={styles.centered} {...values} />
            )}>
            <Step name="TOKEN">
              <LevelUpTokenIssuer
                level={level}
                tokenIdList={tokenIdList}
                className={styles.centered}
                onSuccess={onTokenSuccess}
              />
            </Step>
            <Step name="TFA">
              <LevelUpTfa
                className={classNames(styles.step, styles.cover)}
                {...token}
                onSuccess={forward}
              />
            </Step>
            <Step name="LOADING">
              <LevelUpLoading className={styles.step} {...token} onSuccess={forward} />
            </Step>
            <Step name="COMPLETED">
              <LevelUpResult className={styles.step} {...token} />
            </Step>
          </QueryErrorBoundary>
        </BottomSheet>
      )}
    </StepByStepProvider>
  );
};
